import React, { useState, useContext } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { FiUser, FiMail, FiKey } from "react-icons/fi";
import { UserContext } from "../../context/UserContext";
import Loader from "../../components/Global/Loader";
import PasswordInput from "../../components/Global/PasswordInput";

const SalesPersonAccount = () => {
  const { name, primaryEmail } = useContext(UserContext);
  const [activeTab, setActiveTab] = useState("profile");

  const [emailForm, setEmailForm] = useState({ newEmail: "" });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [submittingEmail, setSubmittingEmail] = useState(false);
  const [submittingPwd, setSubmittingPwd] = useState(false);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const passwordRegex =
    /^(?=.*[A-Z])(?=.*[!@#$%^&*(),.?":{}|<>])[A-Za-z\d!@#$%^&*(),.?":{}|<>]{8,}$/;

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.history.replaceState(null, null, "/signin");
    setTimeout(() => {
      window.location.href = "/signin";
    }, 100);
  };

  const handleChangeEmail = async (e) => {
    e.preventDefault();
    if (!emailRegex.test(emailForm.newEmail)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    try {
      setSubmittingEmail(true);
      const res = await axios.patch(
        `${process.env.REACT_APP_BASE_URL}/auth/change-email`,
        { newEmail: emailForm.newEmail },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      toast.success(res?.data?.message || "Email updated. Please log in again.");
      setTimeout(handleLogout, 1200);
    } catch (err) {
      // toast handled by interceptor
    } finally {
      setSubmittingEmail(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!passwordRegex.test(passwordForm.newPassword)) {
      toast.error(
        "Password must be 8+ chars, include an uppercase letter, a number, and a special character."
      );
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error("New password and Confirm password must match.");
      return;
    }
    try {
      setSubmittingPwd(true);
      const res = await axios.patch(
        `${process.env.REACT_APP_BASE_URL}/auth/change-password`,
        {
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword,
          confirmPassword: passwordForm.confirmPassword,
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      toast.success(
        res?.data?.message || "Password updated. Please log in again."
      );
      setTimeout(handleLogout, 1200);
    } catch (err) {
      // toast handled by interceptor
    } finally {
      setSubmittingPwd(false);
    }
  };

  return (
    <div className="pl-36 mt-16 min-h-screen bg-[#f4f8fb] p-8">
      {(submittingEmail || submittingPwd) && <Loader />}

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h1 className="text-2xl font-semibold text-gray-800">My Account</h1>
        <p className="text-sm text-gray-500 mt-1">
          Manage your sales person account details
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-md">
        <div className="flex border-b border-gray-200">
          <button
            type="button"
            onClick={() => setActiveTab("profile")}
            className={`px-6 py-3 text-sm font-medium border-b-2 flex items-center gap-2 ${
              activeTab === "profile"
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-gray-600 hover:text-gray-800"
            }`}
          >
            <FiUser size={16} /> Profile
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("email")}
            className={`px-6 py-3 text-sm font-medium border-b-2 flex items-center gap-2 ${
              activeTab === "email"
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-gray-600 hover:text-gray-800"
            }`}
          >
            <FiMail size={16} /> Change Email
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("password")}
            className={`px-6 py-3 text-sm font-medium border-b-2 flex items-center gap-2 ${
              activeTab === "password"
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-gray-600 hover:text-gray-800"
            }`}
          >
            <FiKey size={16} /> Change Password
          </button>
        </div>

        <div className="p-6">
          {activeTab === "profile" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-xl">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Name</label>
                <input
                  type="text"
                  value={name || ""}
                  readOnly
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  Email
                </label>
                <input
                  type="text"
                  value={primaryEmail || ""}
                  readOnly
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50"
                />
              </div>
              <p className="text-xs text-gray-500 col-span-full">
                To update your email, use the Change Email tab.
              </p>
            </div>
          )}

          {activeTab === "email" && (
            <form
              onSubmit={handleChangeEmail}
              className="max-w-xl space-y-4"
            >
              <div className="bg-blue-50 border border-blue-200 text-blue-800 text-sm rounded p-3">
                Once your email is updated, you will be logged out. Please sign
                in again with your new email.
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  Current Email
                </label>
                <input
                  type="email"
                  value={primaryEmail || ""}
                  readOnly
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  New Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={emailForm.newEmail}
                  onChange={(e) =>
                    setEmailForm({ newEmail: e.target.value })
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600"
                  placeholder="new@example.com"
                />
              </div>
              <button
                type="submit"
                disabled={submittingEmail || !emailForm.newEmail}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium disabled:opacity-50"
              >
                {submittingEmail ? "Updating..." : "Change Email"}
              </button>
            </form>
          )}

          {activeTab === "password" && (
            <form
              onSubmit={handleChangePassword}
              className="max-w-xl space-y-4"
            >
              <div className="bg-blue-50 border border-blue-200 text-blue-800 text-sm rounded p-3">
                Once your password is updated, you will be logged out. Please
                sign in again with your new password.
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  Current Password <span className="text-red-500">*</span>
                </label>
                <PasswordInput
                  autoComplete="current-password"
                  value={passwordForm.currentPassword}
                  onChange={(e) =>
                    setPasswordForm((p) => ({
                      ...p,
                      currentPassword: e.target.value,
                    }))
                  }
                  inputClassName="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  New Password <span className="text-red-500">*</span>
                </label>
                <PasswordInput
                  autoComplete="new-password"
                  value={passwordForm.newPassword}
                  onChange={(e) =>
                    setPasswordForm((p) => ({
                      ...p,
                      newPassword: e.target.value,
                    }))
                  }
                  inputClassName="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Must be 8+ chars, include uppercase, number and a special
                  character.
                </p>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  Confirm New Password{" "}
                  <span className="text-red-500">*</span>
                </label>
                <PasswordInput
                  autoComplete="new-password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) =>
                    setPasswordForm((p) => ({
                      ...p,
                      confirmPassword: e.target.value,
                    }))
                  }
                  inputClassName="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600"
                />
              </div>
              <button
                type="submit"
                disabled={
                  submittingPwd ||
                  !passwordForm.currentPassword ||
                  !passwordForm.newPassword ||
                  !passwordForm.confirmPassword
                }
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium disabled:opacity-50"
              >
                {submittingPwd ? "Updating..." : "Change Password"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default SalesPersonAccount;
